import React from "react";
import Facts from "components/Facts";
import map from "../map";

import { animations, previousTimeStamp } from "../animation";
import { easingsFunctions } from "../easingsFunctions";
import logLevels from "./logLevels";
import { addRoundedRect } from "./svgUtils";
import { mapDefs, rendererKind, RENDERER_WEBGL } from "../render";

const outerWidth = 100;
const outerHeight = 75;
const outerMargin = 3;

const textSpacing = 1;
const axisFontSize = 10;

const innerWidth = outerWidth - outerMargin * 2;
const innerHeight = outerHeight - outerMargin * 2;

const axisGapLeft = axisFontSize * 2;
const axisGapBottom = axisFontSize * 1.2;
const axisGapRight = 0;
const axisGapTop = axisFontSize / 2;

const graphWidth = innerWidth - axisGapLeft - axisGapRight;
const graphHeight = innerHeight - axisGapBottom - axisGapTop;
const graphLeftX = (graphWidth - axisGapLeft + axisGapRight) * -0.5;
const graphTopY = (graphHeight + axisGapBottom - axisGapTop) * -0.5;
const graphRightX = graphLeftX + graphWidth;
const graphBottomY = graphTopY + graphHeight;

let stagger = 0;

function makeLineGraph(parent, xLabel, yLabel, yMin, yMax) {
  const width = innerWidth;
  const height = innerHeight;

  const e = parent.append("g");

  e.append("rect")
    .attr("fill", "white")
    .attr("stroke", "none")
    .attr("x", width * -0.5)
    .attr("y", height * -0.5)
    .attr("width", width)
    .attr("height", height);

  e.append("path")
    .attr("d", `M${graphLeftX},${graphTopY}v${graphHeight}h${graphWidth}`)
    .attr("stroke", "black")
    .attr("fill", "none")
    .attr("stroke-width", 0.5);

  e.append("text")
    .attr("y", graphBottomY + (axisGapBottom * 1.2) / 2)
    .attr("x", (graphLeftX + graphRightX) / 2)
    .attr("font-size", axisFontSize + "px")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "middle")
    .text(xLabel);

  e.append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "middle")
    .attr("font-size", axisFontSize + "px")
    .attr(
      "transform",
      "translate(" +
        (graphLeftX - axisGapLeft / 2) +
        "," +
        (graphTopY + graphBottomY) / 2 +
        ") rotate(-90)"
    )
    .text(yLabel);

  e.append("text")
    .attr("y", graphTopY)
    .attr("x", graphLeftX - textSpacing)
    .attr("font-size", axisFontSize + "px")
    .attr("text-anchor", "end")
    .attr("dominant-baseline", "middle")
    .text(yMax.toFixed(0));

  e.append("text")
    .attr("y", graphBottomY)
    .attr("x", graphLeftX - textSpacing)
    .attr("font-size", axisFontSize + "px")
    .attr("text-anchor", "end")
    .attr("dominant-baseline", "middle")
    .text(yMin.toFixed(0));

  e.append("path")
    .attr("stroke", "green")
    .attr("stroke-width", 0.5)
    .attr("fill", "none");

  e.append("path")
    .attr("stroke", "blue")
    .attr("stroke-width", 0.5)
    .attr("fill", "none");

  return e;
}

function updateGraph(graph, data) {
  let actualmax = Math.max(...data[1]); // only need the max from the "max" series..
  let suffix = "";
  let max;

  if (actualmax < 100) {
    max = Math.floor(actualmax * 10 + 1) / 10;
    graph.selectAll("text")[2].text(max.toFixed(1));
  } else {
    max = 1.1 * (Math.floor(actualmax) + 1);
    graph.selectAll("text")[2].text(max.toFixed(0));
  }

  let p1 = "M";
  let p2 = "M";
  for (var i = 0; i < 60; i++) {
    const x = graphLeftX + i * (graphWidth / 60);
    const y1 = graphBottomY + ((graphTopY - graphBottomY) * data[0][i]) / max;
    const y2 = graphBottomY + ((graphTopY - graphBottomY) * data[1][i]) / max;
    if (i > 0) {
      p1 += "L";
      p2 += "L";
    }
    p1 += x + "," + y1;
    p2 += x + "," + y2;
  }

  const parts = graph.selectAll("path");
  parts[1].attr("d", p1);
  parts[2].attr("d", p2);
}

function bucketToDate(bucketNum) {
  const min = bucketNum % 100;
  const hour = Math.floor(bucketNum / 100) % 100;
  const day = Math.floor(bucketNum / 10000) % 100;
  const month = Math.floor(bucketNum / 1000000) % 100;
  const year = Math.floor(bucketNum / 100000000);
  const when = new Date(year, month - 1, day, hour, min, 0, 0);
  return when;
}

function dateToBucket(when) {
  const result =
    when.getFullYear() * 100000000 +
    (when.getMonth() + 1) * 1000000 +
    when.getDate() * 10000 +
    when.getHours() * 100 +
    when.getMinutes();
  return result;
}

export default {
  collections: {
    machines: { objectType: "machine" },
  },

  stats: {
    aggregate: ["ram", "cpu"],
    count: ["e0", "e1", "e2", "e3", "e4", "e5"],
  },

  eventHandlers: {
    statbucketupdates: (
      { id, elem: e, stats, eventsgraph, ramgraph, cpugraph },
      newStats
    ) => {
      const updatedStatTypes = {};

      for (const bucketType in newStats) {
        for (const bucketName in newStats[bucketType]) {
          for (const statType in newStats[bucketType][bucketName]) {
            updatedStatTypes[statType[0] == "e" ? "events" : statType] = true;
          }
        }
      }

      const maxBucket = Object.keys(stats.m).reduce(
        (acc, bucketName) => Math.max(acc, parseInt(bucketName)),
        0
      );

      if (updatedStatTypes.ram) {
        let avg, max;
        const data = [(avg = []), (max = [])];
        const start = bucketToDate(maxBucket);
        start.setMinutes(start.getMinutes() - 60); // 1 extra, so with the first +1 we start at -59, and end at 0
        for (var i = 0; i <= 59; i++) {
          start.setMinutes(start.getMinutes() + 1);
          const bucket = stats.m[dateToBucket(start)];
          if (bucket) {
            const ram = bucket.ram;
            if (ram) {
              avg.push((ram.a || 0) / (1024 * 1024));
              max.push((ram.mx || 0) / (1024 * 1024));
              continue;
            }
          }
          avg.push(0);
          max.push(0);
        }
        updateGraph(ramgraph, data);
      }
      if (updatedStatTypes.cpu) {
        let avg, max;
        const data = [(avg = []), (max = [])];
        const start = bucketToDate(maxBucket);
        start.setMinutes(start.getMinutes() - 60); // 1 extra, so with the first +1 we start at -59, and end at 0
        for (var i = 0; i <= 59; i++) {
          start.setMinutes(start.getMinutes() + 1);
          const bucket = stats.m[dateToBucket(start)];
          if (bucket) {
            const cpu = bucket.cpu;
            if (cpu) {
              avg.push(cpu.a || 0);
              max.push(cpu.mx || 0);
              continue;
            }
          }
          avg.push(0);
          max.push(0);
        }
        updateGraph(cpugraph, data);
      }
      if (updatedStatTypes.events) {
        const levelCounts = [0, 0, 0, 0, 0, 0];
        const start = bucketToDate(maxBucket);
        start.setMinutes(start.getMinutes() - 60); // 1 extra, so with the first +1 we start at -59, and end at 0
        for (var i = 0; i <= 59; i++) {
          start.setMinutes(start.getMinutes() + 1);
          const bucket = stats.m[dateToBucket(start)];
          if (!bucket) continue;
          if (bucket.e0) levelCounts[0] += bucket.e0.c;
          if (bucket.e1) levelCounts[1] += bucket.e1.c;
          if (bucket.e2) levelCounts[2] += bucket.e2.c;
          if (bucket.e3) levelCounts[3] += bucket.e3.c;
          if (bucket.e4) levelCounts[4] += bucket.e4.c;
          if (bucket.e5) levelCounts[5] += bucket.e5.c;
        }

        const rects = eventsgraph.selectAll("rect");
        const texts = eventsgraph.selectAll("text");

        const maxCount = Math.max(...levelCounts);
        const barSpace = innerHeight / 2 - axisFontSize - textSpacing;
        for (var i = 0; i < levelCounts.length; i++) {
          const row = Math.floor(i / 3);

          const ht = Math.ceil((barSpace * levelCounts[i]) / maxCount);

          rects[i]
            .attr(
              "y",
              row == 0
                ? -axisFontSize - textSpacing - ht
                : innerHeight / 2 - axisFontSize - textSpacing - ht
            )
            .attr("height", ht);

          texts[i].text(
            levelCounts[i] >= 1000
              ? (levelCounts[i] / 1000).toFixed(1) + "k"
              : levelCounts[i]
          );
        }
      }
    },
  },

  hasRag: true,
  layout: { radius: (outerWidth / 2) * 1.42 * 0.95 },
  render: {
    enter: (item) => {
      const { elem: e, label } = item;

      const clipPathName = "graphClipPath";
      const svgDefs = mapDefs();
      const pathSel = svgDefs.selectAll("#" + clipPathName);
      if (pathSel.length == 0) {
        svgDefs
          .append("clipPath")
          .attr("id", clipPathName)
          .append("rect")
          .attr("x", innerWidth * -0.5)
          .attr("y", innerHeight * -0.5)
          .attr("width", innerWidth)
          .attr("height", innerHeight);
      }

      e.append("text")
        .attr("y", outerHeight * -0.5 - 2)
        .attr("font-size", axisFontSize + "px")
        .attr("x", outerWidth * 0.5 - 1)
        .attr("text-anchor", "end")
        .attr("stroke", "#999")
        .attr("fill", "#999")
        .attr("letter-spacing", 2)
        .text(label.toUpperCase());

      addRoundedRect(
        e,
        outerWidth * -0.5,
        outerHeight * -0.5,
        outerWidth,
        outerHeight,
        5,
        0.5
      )
        .attr("fill", "white")
        .attr("stroke", "black");

      const cont = e
        .append("g")
        .attr("clip-path", "url(#" + clipPathName + ")");

      const g1 = makeLineGraph(cont, "1 hr", "Cores", 0, 1);

      item.cpugraph = g1;

      const g2 = makeLineGraph(cont, "1 hr", "MB", 0, 1).attr(
        "transform",
        "translate(" + innerWidth + ",0)"
      );

      item.ramgraph = g2;

      const evts = cont.append("g");

      const barWidth = innerWidth / 6;

      for (var i = 0; i < 6; i++) {
        const col = i % 3;
        const row = Math.floor(i / 3);

        const ht = 0;

        evts
          .append("rect")
          .attr("fill", logLevels[i].colour)
          .attr("data-props", `{"level":"${logLevels[i].name}"}`)
          .attr(
            "x",
            -innerWidth / 2 + (col + 0.5) * (innerWidth / 3) - barWidth / 2
          )
          .attr(
            "y",
            row == 0
              ? -axisFontSize - textSpacing - ht
              : innerHeight - axisFontSize - textSpacing - ht
          )
          .attr("width", barWidth)
          .attr("height", ht);
        evts
          .append("text")
          .attr("x", -innerWidth / 2 + (innerWidth / 3) * (col + 0.5))
          .attr("y", row == 0 ? -textSpacing : innerHeight / 2 - textSpacing)
          .attr("stroke", logLevels[i].colour)
          .attr("fill", logLevels[i].colour)
          .attr("data-props", `{"level":"${logLevels[i].name}"}`)
          .attr("font-size", axisFontSize + "px")
          .attr("text-anchor", "middle")
          .text("?");
      }

      item.eventsgraph = evts;

      item.pages = [g1, g2, evts];
      item.pageIndex = 0;
      item.nextPageIndex = 1;

      const delay = 5000;

      // Under WebGL the pages turn like a card flipping about its vertical
      // axis, carried by the `data-flip` attribute that only that backend
      // reads; a flip needs a little longer than the slide to read as one.
      // Under SVG the attribute would be inert, so the horizontal slide stays.
      const flip = rendererKind() === RENDERER_WEBGL;
      const duration = flip ? 500 : 300;

      let thisItemOffset = ++stagger * 300;

      function setupForSwitch() {
        item.nextPageIndex = (item.pageIndex + 1) % item.pages.length;
        for (var i = 0; i < item.pages.length; i++) {
          if (i == item.pageIndex) {
            item.pages[i].attr("visibility", "visible").attr("transform", "");
          } else {
            item.pages[i].attr("visibility", "hidden");
            if (flip) item.pages[i].attr("transform", "");
          }
        }
        if (!flip) {
          item.pages[item.nextPageIndex]
            .attr("transform", "translate(" + innerWidth + ",0)")
            .attr("visibility", "visible");
        }

        // get start of next animation slot
        const n = Math.floor(
          (previousTimeStamp - thisItemOffset) / (duration + delay) + 1
        );

        animations.add({
          type: "switch-env-page",
          // Re-arms itself from its own done branch, so without an owner a deleted
          // env - and focus or filter changes delete them in bulk - leaves a chain
          // running for the lifetime of the page.
          owner: item.elem,
          start: thisItemOffset + n * (delay + duration),
          from: 0,
          to: innerWidth,
          duration: duration,
          easing: easingsFunctions.kieren,
          frame: (v, done) => {
            const current = item.pages[item.pageIndex];
            const next = item.pages[item.nextPageIndex];

            if (flip) {
              // The whole card turns about its vertical axis: the front face
              // rotates to edge-on at 90 degrees, where the back face - the
              // same frame carrying the next page - takes over at -90 and
              // unfolds the rest of the way. The easing's early dip below zero
              // reads as the card winding up before it turns.
              const angle = (v / innerWidth) * 180;
              if (angle < 90) {
                e.attr("data-flip", angle);
                current.attr("visibility", "visible");
                next.attr("visibility", "hidden");
              } else {
                e.attr("data-flip", angle - 180);
                current.attr("visibility", "hidden");
                next.attr("visibility", "visible");
              }
            } else {
              current.attr("transform", "translate(" + -v + ",0)");
              next.attr("transform", "translate(" + (innerWidth - v) + ",0)");
            }

            if (done) {
              if (flip) e.attr("data-flip", null);
              item.pageIndex = item.nextPageIndex;
              setupForSwitch();
            }
          },
        });
      }

      // offset = (stagger * 250)
      // duration = 500
      // delay = 5000

      // start(n) = offset + (duration + delay * n)

      // start(n) > now

      // now < offset + (duration + delay) * n
      // now - offset < (duration + delay) * n
      // (now - offset) / n < (duration + delay)
      // n / (now - offset) < 1 / (duration + delay)
      // n < (now - offset) / (duration + delay)

      setupForSwitch();

      return e;
    },
  },

  renderInfoHead: (t) => (t.parent.data || t.parent.label) + " - " + t.label,
  renderInfoBody: (t) => (
    <Facts>
      <Facts.Fact label="App versions" value={getAppVersions(t)} />
      <Facts.Fact label="Saffron versions" value={getSaffVersions(t)} />
    </Facts>
  ),
};

function getAppVersions(t) {
  return (
    Object.keys(map.objects)
      .filter((k) => k.startsWith(t.id))
      .map((k) => map.objects[k])
      .filter((o) => o.type == "process" && o.data)
      .map((o) => JSON.parse(o.data).appVersion)
      .filter((v) => v)
      .reduce(
        (p, c) => (p == "" ? c : p.indexOf(c) == -1 ? p + ", " + c : p),
        ""
      ) || "Unknown"
  );
}

function getSaffVersions(t) {
  return (
    Object.keys(map.objects)
      .filter((k) => k.startsWith(t.id))
      .map((k) => map.objects[k])
      .filter((o) => o.type == "process" && o.data)
      .map((o) => JSON.parse(o.data).saffronVersion)
      .filter((v) => v)
      .reduce(
        (p, c) => (p == "" ? c : p.indexOf(c) == -1 ? p + ", " + c : p),
        ""
      ) || "Unknown"
  );
}
